import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { DownloadSimple, FolderOpen, CaretDown, CaretRight } from "@phosphor-icons/react";
import whcLogo from "../../assets/whcLogo.svg";
import { getFileIconBySize, formatFileSize } from "../../utils/fileUtils";
import {
  downloadSharedFolderFile,
  downloadSharedFolderZip,
  fetchSharedFolderTree,
} from "./sharedFolderApi";
import {
  Card,
  DownloadButton,
  EmptyNotice,
  ErrorText,
  ExpiryNotice,
  FileMeta,
  FileName,
  FileRow,
  FirmName,
  FolderChildren,
  FolderNodeRow,
  FolderTitle,
  Header,
  Page,
  PasswordForm,
  PasswordInput,
  Spinner,
  StateMessage,
  SubmitButton,
  TruncatedNotice,
  ZipButton,
} from "./SharedFolderStyled";

// Public, unauthenticated landing page for a "share a whole folder" link.
// Anyone with the link + (if set) the password sees whatever is currently
// in the folder and its subfolders — this re-fetches the live tree on
// every visit, it is not a snapshot. See documents/views/external_folder_share.py
// on the backend for the exact contract this page talks to.

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const filenameFromDisposition = (headerValue, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(headerValue || "");
  return match ? match[1] : fallback;
};

const formatExpiry = (isoString) => {
  if (!isoString) return "";
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return isoString;
  }
};

const FolderNode = ({ node, depth, onDownloadFile, downloadingId }) => {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasContents =
    (node.folders && node.folders.length > 0) || (node.documents && node.documents.length > 0);

  return (
    <div>
      <FolderNodeRow
        onClick={() => setExpanded((v) => !v)}
        role="button"
        aria-expanded={expanded}
        data-testid={`folder-node-${node.id}`}
      >
        {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
        <FolderOpen size={16} color="#5A6C87" />
        {node.name}
      </FolderNodeRow>
      {expanded && (
        <FolderChildren>
          {!hasContents && <EmptyNotice>This folder is empty.</EmptyNotice>}
          {(node.folders || []).map((child) => (
            <FolderNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onDownloadFile={onDownloadFile}
              downloadingId={downloadingId}
            />
          ))}
          {(node.documents || []).map((doc) => (
            <FileRow key={doc.id} data-testid={`file-row-${doc.id}`}>
              {getFileIconBySize(doc.name, "document", 16)}
              <FileName title={doc.name}>{doc.name}</FileName>
              <FileMeta>{formatFileSize(doc.size)}</FileMeta>
              <DownloadButton
                type="button"
                aria-label={`Download ${doc.name}`}
                disabled={downloadingId === doc.id}
                onClick={() => onDownloadFile(doc)}
              >
                <DownloadSimple size={16} />
              </DownloadButton>
            </FileRow>
          ))}
        </FolderChildren>
      )}
    </div>
  );
};

FolderNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    folders: PropTypes.array,
    documents: PropTypes.array,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  downloadingId: PropTypes.string,
};

const SharedFolder = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | password_required | ready | expired | error
  const [meta, setMeta] = useState(null);
  const [passwordInput, setPasswordInput] = useState("");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [zipDownloading, setZipDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(
    async (passwordAttempt) => {
      setSubmitting(true);
      try {
        const response = await fetchSharedFolderTree(token, passwordAttempt);
        setMeta(response.data);
        setConfirmedPassword(passwordAttempt || "");
        setStatus("ready");
        setPasswordError("");
      } catch (error) {
        const httpStatus = error?.response?.status;
        const data = error?.response?.data;
        if (httpStatus === 401) {
          setStatus("password_required");
          setMeta((prev) => ({ ...(prev || {}), ...(data || {}) }));
        } else if (httpStatus === 403) {
          setStatus("password_required");
          setMeta((prev) => ({ ...(prev || {}), ...(data || {}) }));
          setPasswordError("Incorrect password. Please try again.");
        } else if (httpStatus === 410 || httpStatus === 404) {
          setStatus("expired");
          setErrorMessage(data?.detail || "This link is no longer available.");
        } else {
          setStatus("error");
          setErrorMessage("Something went wrong loading this folder. Please try again later.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [token]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!passwordInput.trim() || submitting) return;
    load(passwordInput.trim());
  };

  const handleDownloadFile = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const response = await downloadSharedFolderFile(token, doc.id, confirmedPassword);
      const filename = filenameFromDisposition(
        response.headers["content-disposition"],
        doc.name
      );
      triggerBlobDownload(new Blob([response.data]), filename);
    } catch {
      setErrorMessage(`Could not download "${doc.name}". Please try again.`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadZip = async () => {
    setZipDownloading(true);
    try {
      const response = await downloadSharedFolderZip(token, confirmedPassword);
      const filename = filenameFromDisposition(
        response.headers["content-disposition"],
        `${meta?.folder_name || meta?.tree?.name || "folder"}.zip`
      );
      triggerBlobDownload(new Blob([response.data]), filename);
    } catch (error) {
      const httpStatus = error?.response?.status;
      if (httpStatus === 413) {
        setErrorMessage(
          "This folder is too large to download as one zip. Download files individually instead."
        );
      } else {
        setErrorMessage("Could not download the zip. Please try again.");
      }
    } finally {
      setZipDownloading(false);
    }
  };

  return (
    <Page>
      <Header>
        <img src={whcLogo} alt="Werner Hoffman Greig" />
        <FirmName>Werner Hoffman Greig</FirmName>
      </Header>

      <Card>
        {status === "loading" && (
          <StateMessage>
            <Spinner />
            Loading shared folder…
          </StateMessage>
        )}

        {status === "expired" && (
          <StateMessage data-testid="shared-folder-expired">{errorMessage}</StateMessage>
        )}

        {status === "error" && (
          <StateMessage data-testid="shared-folder-error">{errorMessage}</StateMessage>
        )}

        {status === "password_required" && (
          <>
            <FolderTitle>{meta?.folder_name || "Shared folder"}</FolderTitle>
            <ExpiryNotice>This folder is password-protected.</ExpiryNotice>
            <PasswordForm onSubmit={handlePasswordSubmit}>
              <PasswordInput
                type="password"
                placeholder="Enter password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                error={Boolean(passwordError)}
                autoFocus
                data-testid="shared-folder-password-input"
              />
              {passwordError && <ErrorText>{passwordError}</ErrorText>}
              <SubmitButton type="submit" disabled={submitting || !passwordInput.trim()}>
                {submitting ? "Checking…" : "View folder"}
              </SubmitButton>
            </PasswordForm>
          </>
        )}

        {status === "ready" && meta && (
          <>
            <FolderTitle>{meta.folder_name}</FolderTitle>
            <ExpiryNotice>
              This link expires on {formatExpiry(meta.expires_at)}.
            </ExpiryNotice>
            {meta.truncated && (
              <TruncatedNotice>
                This folder has more files than can be listed at once (showing the
                first {meta.document_count}). Some files may not be shown — ask the
                sender to share a subfolder instead if you need everything.
              </TruncatedNotice>
            )}
            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
            <ZipButton
              type="button"
              onClick={handleDownloadZip}
              disabled={zipDownloading}
            >
              <DownloadSimple size={16} />
              {zipDownloading ? "Preparing zip…" : "Download all (.zip)"}
            </ZipButton>
            {meta.tree && (
              <FolderNode
                node={meta.tree}
                depth={0}
                onDownloadFile={handleDownloadFile}
                downloadingId={downloadingId}
              />
            )}
          </>
        )}
      </Card>
    </Page>
  );
};

export default SharedFolder;
